import { useState, useEffect } from "react";
import { LegalLayout } from "../components/LegalLayout";
import { Seo } from "../components/Seo";
import { Check, X } from "lucide-react";

const H2 = ({ children }) => (
  <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white mt-12 mb-4">
    {children}
  </h2>
);

export default function Cookies() {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("pdr-cookie-consent");
    setConsent(stored);
  }, []);

  const reset = () => {
    localStorage.removeItem("pdr-cookie-consent");
    setConsent(null);
    window.location.reload();
  };

  return (
    <>
      <Seo
        title="Politique de cookies — PDR France"
        description="Politique de cookies de PDR France, conforme CNIL. Gérez vos préférences de consentement."
        path="/cookies"
      />
      <LegalLayout
      title="Politique de cookies"
      eyebrow="Gestion des cookies"
      lastUpdate="Décembre 2025"
    >
      <p>
        Cette politique de cookies explique ce que sont les cookies,
        comment nous les utilisons sur le site pdrfrance.fr et
        comment vous pouvez contrôler leur utilisation.
      </p>

      <H2>1. Qu'est-ce qu'un cookie ?</H2>
      <p>
        Un cookie est un petit fichier texte déposé sur votre terminal
        (ordinateur, tablette, smartphone) lors de votre visite sur un
        site internet. Il permet au site de reconnaître votre
        navigateur, de conserver certaines informations et d'améliorer
        votre expérience de navigation.
      </p>

      <H2>2. Cookies utilisés sur ce site</H2>
      <p>Nous utilisons les catégories de cookies suivantes :</p>

      <div className="space-y-4 my-6">
        <div className="border border-white/10 bg-[#111111] p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs font-bold uppercase tracking-widest text-[#007AFF]">
              Essentiels
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">
              · Non désactivables
            </div>
          </div>
          <p className="text-sm text-white/70">
            Nécessaires au bon fonctionnement du site : mémorisation de
            votre choix de consentement cookies, sécurité, sessions.
            Ces cookies ne nécessitent pas de consentement préalable
            conformément aux recommandations de la CNIL.
          </p>
        </div>

        <div className="border border-white/10 bg-[#111111] p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs font-bold uppercase tracking-widest text-[#EF4135]">
              Mesure d'audience
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">
              · Soumis à consentement
            </div>
          </div>
          <p className="text-sm text-white/70">
            Cookies permettant de mesurer la fréquentation du site, les
            pages visitées, et d'améliorer le service. Ils ne sont
            déposés qu'après votre consentement explicite.
          </p>
        </div>

        <div className="border border-white/10 bg-[#111111] p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs font-bold uppercase tracking-widest text-white">
              Marketing
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">
              · Soumis à consentement
            </div>
          </div>
          <p className="text-sm text-white/70">
            Cookies utilisés pour afficher des contenus personnalisés ou
            mesurer l'efficacité de campagnes publicitaires. Déposés
            uniquement avec votre consentement.
          </p>
        </div>
      </div>

      <H2>3. Votre consentement</H2>
      <p>
        Lors de votre première visite, un bandeau vous permet
        d'accepter ou de refuser le dépôt de cookies non essentiels.
        Votre choix est mémorisé pendant 6 mois. Vous pouvez modifier
        vos préférences à tout moment ci-dessous.
      </p>

      <div className="my-10 border border-white/10 bg-[#111111] p-6 md:p-8">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#007AFF] mb-3">
          — Vos préférences actuelles
        </div>
        {consent === "accepted" && (
          <div className="flex items-center gap-2 text-green-400 mb-5">
            <Check className="w-5 h-5" />
            <span className="font-semibold">Cookies acceptés</span>
          </div>
        )}
        {consent === "refused" && (
          <div className="flex items-center gap-2 text-[#EF4135] mb-5">
            <X className="w-5 h-5" />
            <span className="font-semibold">Cookies refusés</span>
          </div>
        )}
        {!consent && (
          <div className="text-white/60 mb-5 text-sm">
            Aucune préférence enregistrée. Le bandeau sera affiché à
            votre prochaine visite.
          </div>
        )}
        <button
          onClick={reset}
          data-testid="reset-cookie-consent"
          className="bg-[#007AFF] hover:bg-[#3B82F6] text-white text-xs font-bold uppercase tracking-widest px-6 py-3 transition-colors"
        >
          Modifier mes préférences
        </button>
      </div>

      <H2>4. Gérer les cookies depuis votre navigateur</H2>
      <p>
        Vous pouvez également configurer votre navigateur pour refuser
        tous les cookies ou vous notifier à chaque dépôt :
      </p>
      <ul className="list-disc list-inside space-y-1 text-white/75">
        <li>
          <a
            href="https://support.google.com/chrome/answer/95647"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#007AFF] hover:underline"
          >
            Google Chrome
          </a>
        </li>
        <li>
          <a
            href="https://support.mozilla.org/fr/kb/protection-renforcee-contre-pistage-firefox-ordinateur"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#007AFF] hover:underline"
          >
            Mozilla Firefox
          </a>
        </li>
        <li>
          <a
            href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#007AFF] hover:underline"
          >
            Safari
          </a>
        </li>
        <li>
          <a
            href="https://support.microsoft.com/fr-fr/windows/supprimer-et-g%C3%A9rer-les-cookies-168dab11-0753-043d-7c16-ede5947fc64d"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#007AFF] hover:underline"
          >
            Microsoft Edge
          </a>
        </li>
      </ul>

      <H2>5. Durée de conservation</H2>
      <p>
        La durée de vie des cookies déposés n'excède pas 13 mois,
        conformément aux recommandations de la CNIL. Votre consentement
        est mémorisé pendant 6 mois.
      </p>

      <H2>6. Plus d'informations</H2>
      <p>
        Pour en savoir plus sur les cookies et vos droits, vous pouvez
        consulter le site de la{" "}
        <a
          href="https://www.cnil.fr/fr/cookies-et-traceurs-que-dit-la-loi"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#007AFF] hover:underline"
        >
          CNIL
        </a>
        .
      </p>
    </LegalLayout>
    </>
  );
}
